import { Box, Typography, Avatar } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';

/**
 * Renders a single chat message bubble with sender information, timestamp, and optional avatar.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.message - The text content of the chat message.
 * @param {string} props.time - The timestamp of when the message was sent.
 * @param {boolean} props.isCurrentUser - Whether the message was sent by the current user.
 * @param {Object} props.conversationUser - The user object for the conversation partner.
 * @param {string} props.conversationUser.avatar - The avatar URL of the conversation user.
 * @param {string} props.sender - The name of the sender of the message.
 * @returns {JSX.Element} The rendered chat message component.
 */
const ChatMessage = ({
  message,
  time,
  isCurrentUser,
  conversationUser,
  sender,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginY: '10px',
        marginX: '0px',
        flexGrow: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
      >
        {!isCurrentUser && (
          <Box sx={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
            <Avatar
              src={conversationUser.avatar}
              sx={{ marginRight: '10px' }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: isCurrentUser ? '#CCE5FF' : '#E9E9E9',
            borderRadius: isCurrentUser
              ? '10px 10px 0px 10px'
              : '10px 10px 10px 0px',
            padding: '5px',
            width: '46%',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.1)',
            borderBottom: '1px solid #D3D3D3',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              paddingLeft: '5px',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap', // This preserves newlines and spaces
                wordBreak: 'break-word',
              }}
            >
              <b>{`${sender}: `}</b>
              {message}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ marginTop: '2px' }}
            >
              {time}
            </Typography>
            {isCurrentUser && (
              <DoneAllIcon sx={{ color: 'gray', fontSize: '14px' }} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatMessage;
